import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadFile } from '@wonderschool/file-service-client';

import DocumentUploader from '../../../Components/Upload/DocumentUploader';

import { FormComponentProps } from '../../../common';
import { logError } from '../../../rollbar';

import { DocumentUploadIcon } from '@wonderschool/common-base-ui';
import { allowedMimeTypes } from '../../../documents/dictionary';
import { useOrganization } from '../../../hooks/useOrganizations';
import { resolveEnrollmentDocuments } from '../../enrollmentDocuments';
import { DocumentType, InvitationRecipientType } from '../../types';

import { FileCategory, FileDocument } from '@wonderschool/common-base-types';
import { Button } from '@wonderschool/common-base-ui';
import { UseFormReturn } from 'react-hook-form';
import { StaffDocumentsTable as DocumentsTable } from '../../../documents';

type EnrollmentFormValues = {
  documents: DocumentType[];
  invitationRecipients: InvitationRecipientType[];
};

type EnrollmentDocumentsFormProps = FormComponentProps<DocumentType[]> & {
  documents?: DocumentType[];
  enrollmentForm: UseFormReturn<EnrollmentFormValues>;
};

const EnrollmentDocumentsForm: React.FC<EnrollmentDocumentsFormProps> = ({
  title,
  documents,
  onSave,
  enrollmentForm,
}) => {
  const { t } = useTranslation();
  const organization = useOrganization();
  const { uploadFile, error: uploadError, isSuccess: uploadSuccess, reset: resetUploadHook } = useUploadFile();
  const { id: currentOrganizationId } = useOrganization();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentType[]>(documents ?? []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const validate = useCallback(() => {
    const errorsLocal: Record<string, string> = {};
    // Documents are no longer required - no validation needed
    return errorsLocal;
  }, []);

  useEffect(() => {
    const errorsLocal = validate();
    setErrors(errorsLocal);
    if (Object.keys(errorsLocal).length > 0) {
      onSave?.({ errors: errorsLocal });
    } else {
      onSave?.({ data: selectedDocuments });
    }
  }, [onSave, selectedDocuments, validate]);

  useEffect(() => {
    if (uploadSuccess && fileToUpload) {
      setFileToUpload(null);
      resetUploadHook();
    }
  }, [fileToUpload, resetUploadHook, uploadSuccess]);

  // Sync local state with form state when component mounts or form documents change
  useEffect(() => {
    const formDocuments = enrollmentForm.getValues('documents');
    if (formDocuments && formDocuments.length > 0) {
      setSelectedDocuments(formDocuments);
    }
  }, [enrollmentForm]);

  const handleSelectedDocuments = useCallback(
    async (selectedDocuments: FileDocument[]) => {
      const selectedDocumentsLocal = await resolveEnrollmentDocuments(selectedDocuments);
      setSelectedDocuments(selectedDocumentsLocal);
      enrollmentForm.setValue('documents', selectedDocumentsLocal);
    },
    [enrollmentForm]
  );

  const onFileSelected = useCallback(({ data: file }: { data: File }) => {
    if (file) {
      setFileToUpload(file);
    }
  }, []);

  const onFileSubmit = useCallback(async () => {
    if (!fileToUpload) {
      return null;
    }

    try {
      uploadFile({
        file: fileToUpload,
        options: {
          fileAction: 'downloadable',
          fileCategory: FileCategory.FORM,
          metadata: {
            uploadPath: `/organizations/${currentOrganizationId}`,
          },
        },
      });
    } catch (error) {
      logError('Documents - Upload error: ', {
        clientError: error,
        serverMessage: uploadError,
      });
    }

    return null;
  }, [currentOrganizationId, fileToUpload, uploadError, uploadFile]);

  return (
    <div className="space-y-4" data-testid="docs-upload-wrap">
      <div className="flex items-center justify-between" data-testid="docs-upload-title">
        <h2 className="text-2xl font-bold">
          {t(title ?? 'enrollments.documentsFormTitle', { name: organization.name })}
        </h2>
        <DocumentUploader
          onFileSelected={onFileSelected}
          onSubmit={onFileSubmit}
          onError={() => null}
          onClose={() => setIsModalOpen(false)}
          shouldUseExternalStorageApi
          title={t('enrollments.documents.uploadAction')}
          allowedFileTypes={allowedMimeTypes}
          maxNumberOfFiles={1}
          modalOverrideProps={{
            trigger: <UploadLink />,
            open: isModalOpen,
          }}
          data-testid="docs-uploader"
        />
      </div>

      <DocumentsTable
        onRowSelect={handleSelectedDocuments}
        hideBanner={true}
        hideActions={true}
        selectedDocumentIds={selectedDocuments.map((document) => document.id)}
        selectedRows={enrollmentForm.watch('documents').map(
          (document) =>
            ({
              id: document.id,
              name: document.name,
              fileURL: document.url ?? null,
            }) as FileDocument
        )}
      />
    </div>
  );

  function UploadLink() {
    return (
      <div>
        <Button
          onClick={() => {
            setIsModalOpen(true);
          }}
          data-testid="docs-icon-link"
        >
          <DocumentUploadIcon className="mr-2 size-5" />
          <span>{t('enrollments.documentUploadButton')}</span>
        </Button>
      </div>
    );
  }
};

export default EnrollmentDocumentsForm;
